import { readSSE, type SSEEvent } from '@/utils/sse'

const BASE_URL = 'https://api.dianziyang.com/api/jingque/admin'

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function onUnauthorized() {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_user')
  window.location.href = '/'
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    if (res.status === 401) {
      onUnauthorized()
      throw new Error('未登录或登录已过期')
    }
    if (res.status === 403) throw new Error('无权限访问')
    const text = await res.text()
    let msg = ''
    try {
      const json = JSON.parse(text)
      msg = json.error || json.message || ''
    } catch {
      /* 非 JSON 响应 */
    }
    if (!msg) msg = text.length <= 200 ? text : `请求失败: ${res.status}`
    throw new Error(msg)
  }
  return res.json()
}

// ============ Types ============

// Seed Users SSE 流式进度事件(由后端 SeedPoolUsers handler 推送)
export interface SeedItem {
  userId: string
  nickname: string
  questionnaireId: string
  status: string
  error?: string
}

export interface SeedProgressStart {
  type: 'start'
  total: number
  concurrency: number
}

export interface SeedProgressItem {
  type: 'item'
  index: number
  total: number
  item: SeedItem
}

export interface SeedProgressSummary {
  type: 'summary'
  created: number
  failed: number
  total: number
}

export interface SeedProgressHandlers {
  onStart?: (e: SeedProgressStart) => void
  onItem?: (e: SeedProgressItem) => void
  onSummary?: (e: SeedProgressSummary) => void
  onError?: (msg: string) => void
  onDone?: () => void
}

export interface ChatTemplate {
  id: string
  templateId: string
  name: string
  description: string
  systemPrompt: string
  userPrompt: string
  requiredVariables: string[]
  connectorId: string
  timeoutSeconds: number
  tools: string[]
  // doc 125:模板级FS挂载白名单;undefined/null=不限制,非空=白名单
  fsAllowedMounts?: string[]
  maxAgentRounds: number
  sortOrder: number
  status: string
  sttProvider?: string
  ttsProvider?: string
  ttsVoice?: string
  ttsEmotion?: string
  ttsEmotionScale?: number
  ttsSpeechRate?: number
  createdAt: string
  updatedAt: string
}

export interface CreateChatTemplateRequest {
  templateId: string
  name: string
  description?: string
  systemPrompt?: string
  userPrompt: string
  requiredVariables?: string[]
  connectorId?: string
  timeoutSeconds?: number
  tools?: string[]
  // doc 125:不传=不限制,传非空数组=白名单
  fsAllowedMounts?: string[]
  maxAgentRounds?: number
  sttProvider?: string
  ttsProvider?: string
  ttsVoice?: string
  ttsEmotion?: string
  ttsEmotionScale?: number
  ttsSpeechRate?: number
}

export interface UpdateChatTemplateRequest {
  name?: string
  description?: string
  systemPrompt?: string
  userPrompt?: string
  requiredVariables?: string[]
  connectorId?: string
  timeoutSeconds?: number
  tools?: string[]
  // doc 125:不传=不改;传[]=清除限制(回 NULL);传非空=设置白名单
  fsAllowedMounts?: string[]
  maxAgentRounds?: number
  status?: string
  sttProvider?: string
  ttsProvider?: string
  ttsVoice?: string
  ttsEmotion?: string
  ttsEmotionScale?: number
  ttsSpeechRate?: number
}

export interface LLMConnector {
  id: string
  connectorId: string
  connectorName: string
  modelId: number
  modelName: string
  apiKeyId: number
  apiKeyName: string
  maxTokens: number
  temperature?: number
  topP?: number
  timeoutSeconds: number
  status: string
  extraParams?: Record<string, any>
  baseUrl?: string
  createdAt: string
  updatedAt: string
}

export interface CreateConnectorRequest {
  connectorId: string
  connectorName: string
  modelId: number
  apiKeyId: number
  maxTokens?: number
  temperature?: number
  topP?: number
  timeoutSeconds?: number
}

export interface UpdateConnectorRequest {
  connectorName?: string
  modelId?: number
  apiKeyId?: number
  maxTokens?: number
  temperature?: number
  topP?: number
  timeoutSeconds?: number
  status?: string
}

// ===== Tool Definition =====
export interface ToolDefinition {
  id: string
  toolName: string
  description: string
  parameters: Record<string, any>
  executionLocation: 'agent' | 'frontend'
  timeoutSeconds: number
  noreply: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateToolDefRequest {
  toolName: string
  description?: string
  parameters?: Record<string, any>
  executionLocation: string
  timeoutSeconds?: number
  noreply?: boolean
}

export interface UpdateToolDefRequest {
  toolName?: string
  description?: string
  parameters?: Record<string, any>
  executionLocation?: string
  timeoutSeconds?: number
  noreply?: boolean
}

// ===== Agent Harness 会话项(97 号) =====
export interface HarnessSessionItem {
  session_id: string
  user_id: string
  system_id: string
  message_count: number
  title: string
  created_at: string
  updated_at: string
  last_active_at: string
}

// ===== LLM Model =====
export interface LLMModel {
  id: string
  modelCode: string
  modelName: string
  providerId: number
  providerCode: string
  providerName: string
  supportsVision: boolean
  supportsTools: boolean
  supportsStream: boolean
  maxContextTokens: number
  defaultMaxTokens: number
  defaultTemperature: number
  defaultTopP: number
  defaultTimeoutSeconds: number
  description: string
  status: string
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface CreateModelRequest {
  modelCode: string
  modelName: string
  providerId: number
  supportsTools?: boolean
  supportsStream?: boolean
  supportsVision?: boolean
  maxContextTokens?: number
  defaultMaxTokens?: number
  defaultTemperature?: number
  defaultTopP?: number
  defaultTimeoutSeconds?: number
  description?: string
  sortOrder?: number
}

export interface UpdateModelRequest {
  modelName?: string
  supportsTools?: boolean
  supportsStream?: boolean
  supportsVision?: boolean
  maxContextTokens?: number
  defaultMaxTokens?: number
  defaultTemperature?: number
  defaultTopP?: number
  defaultTimeoutSeconds?: number
  description?: string
  status?: string
  sortOrder?: number
}

// ===== LLM Provider =====
export interface LLMProvider {
  id: string
  providerCode: string
  providerName: string
  defaultBaseUrl: string
  apiFormat: string
  description: string
  status: string
  createdAt: string
  updatedAt: string
}

export interface CreateProviderRequest {
  providerCode: string
  providerName: string
  defaultBaseUrl?: string
  apiFormat: string
  description?: string
}

export interface UpdateProviderRequest {
  providerName?: string
  defaultBaseUrl?: string
  apiFormat?: string
  description?: string
  status?: string
}

// ===== TTS Voice =====
export interface TTSVoice {
  id: string
  provider: string
  voiceId: string
  displayName: string
  gender: string
  language: string
  description: string
  sampleUrl: string
  status: string
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface CreateTTSVoiceRequest {
  provider: string
  voiceId: string
  displayName: string
  gender?: string
  language?: string
  description?: string
  sampleUrl?: string
  sortOrder?: number
}

export interface UpdateTTSVoiceRequest {
  provider?: string
  voiceId?: string
  displayName?: string
  gender?: string
  language?: string
  description?: string
  sampleUrl?: string
  status?: string
  sortOrder?: number
}

// ===== FlowController =====

export interface TopicConfig {
  name: string
  module?: string
  goal: string
  min_turns: number
  max_turns: number
  note: string
  directive: string
}

export interface FlowController {
  id: string
  controllerId: string
  name: string
  description: string
  topics: TopicConfig[]
  directiveContinue: string
  directiveTransition: string
  directiveClosing: string
  openingDirective: string
  reconnectDirective: string
  assessorTemplateId: string
  endMessage: string
  status: string
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export type FlowControllerItem = FlowController

export interface CreateFlowControllerRequest {
  controllerId: string
  name: string
  description?: string
  topics: TopicConfig[]
  directiveContinue?: string
  directiveTransition?: string
  directiveClosing?: string
  openingDirective?: string
  reconnectDirective?: string
  assessorTemplateId?: string
  endMessage?: string
  sortOrder?: number
}

export interface UpdateFlowControllerRequest {
  controllerId?: string
  name?: string
  description?: string
  topics?: TopicConfig[]
  directiveContinue?: string
  directiveTransition?: string
  directiveClosing?: string
  openingDirective?: string
  reconnectDirective?: string
  assessorTemplateId?: string
  endMessage?: string
  status?: string
  sortOrder?: number
}

export interface FlowControllerState {
  phase: string
  currentTopicIndex: number
  currentTopicName: string
  turnCount: number
  totalTopics: number
  completedTopics: number
  lastSufficient: boolean
  ended: boolean
}

export interface FlowControllerLog {
  id: string
  eventType: 'directive' | 'assessor' | 'flow_ended'
  phase: string
  topicIdx: number
  turnCount: number
  lastSufficient: boolean
  directiveType?: string
  directiveText?: string
  topicName?: string
  userInput?: string
  assessorResult?: boolean
  assessorError?: string
  assessorDurationMs?: number
  endMessage?: string
  createdAt: string
}

export interface ControllerSession {
  sessionId: string
  userId: string
  controllerId: string
  messageCount: number
  isEnded: boolean
  createdAt: string
  lastActiveAt: string
}

export interface SessionMessage {
  messageId: string
  messageType: 'user' | 'assistant' | 'tool'
  content: string
  createdAt: string
  topicIndex?: number
  topicModule?: string
}

// ===== LLM API Key =====
export interface LLMApiKey {
  id: string
  providerId: number
  providerCode: string
  providerName: string
  keyName: string
  keyOwner: string
  dailyQuota: number
  monthlyQuota: number
  totalRequests: number
  lastUsedAt: string
  status: string
  expiresAt: string
  createdAt: string
  updatedAt: string
}

export interface CreateAPIKeyRequest {
  providerId: number
  apiKey: string
  keyName?: string
  keyOwner?: string
}

export interface UpdateAPIKeyRequest {
  keyName?: string
  keyOwner?: string
  status?: string
}

export interface AnalyzeTemplate {
  id: string
  templateId: string
  name: string
  description: string
  status: string
  itemCount: number
  items: AnalyzeTemplateItem[]
  createdAt: string
  updatedAt: string
}

export interface AnalyzeTemplateItem {
  id: string
  role: string
  chatTemplateId: string
  propertyName: string
  dependsOn: string
  minInputLength: number
  displayOrder: number
}

export interface CreateAnalyzeTemplateRequest {
  templateId: string
  name: string
  description?: string
  items: {
    role: string
    chatTemplateId: string
    propertyName: string
    dependsOn?: string
    minInputLength?: number
    displayOrder?: number
  }[]
}

export interface UpdateAnalyzeTemplateRequest {
  name?: string
  description?: string
  status?: string
  items?: CreateAnalyzeTemplateRequest['items']
}

export interface AnalyzeTask {
  id: string
  taskId: string
  userId: string
  templateId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  totalItems: number
  successCount: number
  failedCount: number
  errorMessage: string
  variables: Record<string, string>
  results: AnalyzeTaskResult[]
  startedAt: string
  completedAt: string
  createdAt: string
}

export interface AnalyzeTaskResult {
  id: string
  propertyName: string
  result: string
  promptTokens: number
  completionTokens: number
  agentRounds: number
  error: string
}

export interface SystemConfig {
  id: string
  key: string
  value: string
  valueType: string
  description: string
  createdAt: string
  updatedAt: string
}

export interface CreateConfigRequest {
  key: string
  value: string
  valueType: string
  description?: string
}

export interface UpdateConfigRequest {
  value?: string
  description?: string
  changeReason?: string
}

export interface ConfigHistory {
  id: string
  configId: string
  key: string
  oldValue: string
  newValue: string
  changedBy: string
  changeReason: string
  changedAt: string
}

export interface CategoryForest {
  id: string
  code: string
  name: string
  description: string
  sortOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateForestRequest {
  code: string
  name: string
  description?: string
  sourceForestId?: string
}

export interface CategoryTreeNode {
  id: string
  forestId: string
  parentId: string | null
  name: string
  value: string
  description: string
  nodeType: 'folder' | 'leaf'
  sortOrder: number
  enabled: boolean
  children: CategoryTreeNode[]
  categoryType: CategoryType | null
}

export interface CategoryType {
  id: string
  treeNodeId: string
  name: string
  description: string
  extractionPrompt: string
  sortOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface UpdateCategoryTypeRequest {
  name?: string
  description?: string
  extractionPrompt?: string
  enabled?: boolean
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ============ Queue & Scheduler Types ============

export interface QueueInfo {
  queue: string
  size: number
  latencyMs: number
  pending: number
  active: number
  scheduled: number
  retry: number
  archived: number
  completed: number
  processed: number
  failed: number
  paused: boolean
  timestamp: string
}

export interface QueueTask {
  id: string
  type: string
  payload: any
  state: string
  maxRetry: number
  retried: number
  lastErr?: string
  lastFailedAt?: string
  nextProcessAt?: string
  timeoutSecs: number
  deadline?: string
}

export interface SchedulerInfo {
  spec: string
  taskType: string
  queue: string
  opts: Record<string, any>
  description: string
  alive: boolean
  lastSeenAt?: string
}

// ============ Prompt Test ============

export interface PromptTestRunRequest {
  template_id?: string
  system_prompt?: string
  user_prompt?: string
  connector_id?: string
  variables?: Record<string, string>
  timeout_seconds?: number
  disable_thinking?: boolean
}

export interface PromptTestBatchItem {
  id: string
  userId: string
  nickname: string
  status: 'pending' | 'running' | 'success' | 'failed'
  result?: string
  error?: string
  promptTokens: number
  completionTokens: number
}

export interface PromptTestBatch {
  id: string
  activityId: string
  connectorId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  totalItems: number
  successCount: number
  failedCount: number
  errorMessage?: string
  startedAt?: string
  completedAt?: string
  createdAt: string
  items: PromptTestBatchItem[]
}

export interface PromptTestBatchSummary {
  id: string
  activityId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  totalItems: number
  successCount: number
  failedCount: number
  userPreview: string
  createdAt: string
  startedAt?: string
  completedAt?: string
}

export interface PromptTestBatchList {
  items: PromptTestBatchSummary[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// doc 71:批量匹配测试(A,B 对)
export interface PromptTestMatchBatchItem {
  id: string
  userIdA: string
  nicknameA: string
  userIdB: string
  nicknameB: string
  status: 'pending' | 'running' | 'success' | 'failed'
  result?: string
  error?: string
  promptTokens: number
  completionTokens: number
}

export interface PromptTestMatchBatch {
  id: string
  activityId: string
  connectorId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  totalItems: number
  successCount: number
  failedCount: number
  errorMessage?: string
  startedAt?: string
  completedAt?: string
  createdAt: string
  items: PromptTestMatchBatchItem[]
}

export interface PromptTestMatchBatchSummary {
  id: string
  activityId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  totalItems: number
  successCount: number
  failedCount: number
  pairPreview: string
  createdAt: string
  startedAt?: string
  completedAt?: string
}

export interface PromptTestMatchBatchList {
  items: PromptTestMatchBatchSummary[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ============ Match Results Types ============

export interface MatchRecommendation {
  id: string
  seekerUserId: string
  seekerNickname: string
  targetUserId: string
  targetNickname: string
  score: number
  status: string
  analysis?: Record<string, any>
  createdAt: string
}

export interface MatchResultItem {
  id: string
  activityId: string
  profileAId: string
  profileBId: string
  profileAUserId?: string
  profileBUserId?: string
  profileANickname: string
  profileBNickname: string
  score: number
  analysis?: Record<string, any>
  createdAt: string
}

export interface RematchResult {
  enqueued: number
}

// ============ Match Pipeline Logs ============

export interface MatchPipelineLog {
  id: string
  taskId?: string
  activityId: string
  userId: string
  seekerProfileId?: string
  stage: string
  status: string
  detail?: Record<string, any>
  durationMs: number
  error?: string
  createdAt: string
}

// ============ Activity Types ============

export interface Activity {
  id: string
  organizerId: string
  organizerName: string
  flowControllerId: string
  name: string
  description: string
  status: string
  mode?: string // event | profile
  maxParticipants: number
  maxMale: number
  maxFemale: number
  currentParticipants: number
  matchStartTime: string
  matchEndTime: string
  eventStartTime: string
  eventEndTime: string
  eventLocation: string
  chatStartTime: string
  chatEndTime: string
  feedbackStartTime: string
  feedbackEndTime: string
  deleteAfterDays: number
  createdAt: string
  updatedAt: string
}

export interface ActivityDetail extends Activity {
  activityIntro: Record<string, unknown>
}

// ===== Feedback (admin) =====

export interface RatingAverages {
  overall: number
  aiChat: number
  match: number
  eventHalfPoem: number
  event1v1: number
  eventGroupGame: number
  eventMultiTalk: number
}

export interface RetentionStat {
  yes: number
  no: number
}

export interface FeedbackOverview {
  totalParticipants: number
  ratingCount: number
  participationRate: number
  channelCounts: Record<string, number>
  ratingAverages: RatingAverages
  retention: RetentionStat
}

export interface AdminRatingItem {
  userId: string
  nickname: string
  overallScore: number
  aiChatScore: number
  matchScore: number
  eventHalfPoemScore: number
  event1v1Score: number
  eventGroupGameScore: number
  eventMultiTalkScore: number
  shortcomings?: string
  highlights?: string
  expectedSegments?: string
  updatedAt: string
}

export interface AdminSelectionItem {
  fromUserId: string
  fromNickname: string
  toUserId: string
  toNickname: string
  mutual: boolean
  createdAt: string
}

export interface AdminMessageItem {
  id: string
  fromUserId: string
  fromNickname: string
  toUserId: string
  toNickname: string
  content: string
  createdAt: string
}

// ===== Check-In =====

export interface CheckInSession {
  id: string
  name: string
  code: string
  qrUrl: string
  windowStatus: 'not_started' | 'open' | 'ended'
  checkedIn: number
  createdAt: string
}

export interface CheckInRecord {
  userId: string
  nickname: string
  gender: string
  age: number
  checkedAt: string
}

export interface CheckInOverview {
  totalRegistered: number
  completedConversation: number
  checkedIn: number
}

export interface CreateActivityRequest {
  organizerId: string
  flowControllerId: string
  name: string
  description?: string
  mode?: string // event | profile；空值按 event 处理
  maxParticipants?: number
  maxMale?: number
  maxFemale?: number
  matchStartTime: string
  matchEndTime: string
  eventStartTime: string
  eventEndTime: string
  eventLocation?: string
  deleteAfterDays?: number
  activityIntro?: Record<string, unknown>
  chatStartTime: string
  chatEndTime?: string
  feedbackStartTime?: string
  feedbackEndTime: string
}

export interface UpdateActivityRequest {
  name?: string
  description?: string
  maxParticipants?: number
  maxMale?: number
  maxFemale?: number
  matchStartTime?: string
  matchEndTime?: string
  eventStartTime?: string
  eventEndTime?: string
  eventLocation?: string
  deleteAfterDays?: number
  activityIntro?: Record<string, unknown>
  chatStartTime?: string
  chatEndTime?: string
  feedbackStartTime?: string
  feedbackEndTime?: string
}

// ===== Report (profile 模式产物) =====

export interface ReportSummary {
  id: string
  questionnaireId: string
  userId: string
  nickname?: string
  occupation?: string
  mbti?: string
  status: string
  createdAt: string
  updatedAt: string
}

export interface ReportDetail extends ReportSummary {
  haveTags?: string[]
  wantTags?: string[]
  haveInfo?: string
  wantInfo?: string
  notHaveInfo?: string
  notWantInfo?: string
  personalitySummary?: string
  lifestyleSummary?: string
  relationshipNeeds?: string
  reportContent?: Record<string, unknown>
}

// ============ Questionnaire Types (Admin) ============

export interface QuestionnaireSummary {
  id: string
  userId: string
  status: 'registered' | 'completed' | 'waitlist'
  gender?: string
  basicInfo: Record<string, unknown>
  contextId?: string
  submittedAt: string
  conversationSubmittedAt?: string
  createdAt: string
  wechatAdded: boolean
  disabled: boolean
  profileStatus: 'pending' | 'active'
  mbti?: string
}

export interface QuestionnaireDetail extends QuestionnaireSummary {
  flowControllerId: string
  activityId?: string
  conversation?: Record<string, unknown>
}

export interface BirthYearBucket {
  name: string
  count: number
}

export interface QuestionnaireStats {
  totalActive: number
  registered: number
  completed: number
  notAddedWechat: number
  profilePending: number
  profileActive: number
  disabledCount: number
  waitlistCount: number
  genderCounts: Record<string, number>
  birthYearBuckets: BirthYearBucket[]
}

export interface QuestionnaireListResponse {
  items: QuestionnaireSummary[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  stats: QuestionnaireStats
}

export interface MatchProfileDTO {
  gender?: string
  age?: number
  height?: number
  education?: string
  schoolType?: string
  city?: string

  haveTags?: string[]
  wantTags?: string[]

  haveInfo?: string
  wantInfo?: string
  notHaveInfo?: string
  notWantInfo?: string

  personalitySummary?: string
  lifestyleSummary?: string
  relationshipNeeds?: string

  mbti?: string

  prefMinAge?: number
  prefMaxAge?: number
  prefMinHeight?: number
  prefMaxHeight?: number
  prefEducation?: string[]
  prefCities?: string[]

  status: string
  createdAt: string
  updatedAt: string
}

export interface UserProfileDetail {
  userId: string
  nickname?: string
  profile: MatchProfileDTO | null
  activityId?: string
  conversation?: Record<string, unknown>
}

// ============ Organizer Type ============

export interface Organizer {
  id: string
  userId: string
  orgName: string
  contactName: string
  contactPhone: string
  status: string
}

// ============ API Methods ============

export const jingqueAdminApi = {
  // ---- Seed Users(批量造 pool 测试用户,SSE 流式)----
  seedPoolUsers: async (
    activityId: string,
    req: {
      personas: Array<{
        nickname: string
        gender: string
        age: number
        height: number
        education: string
        city: string
        occupation: string
        persona: string
        conversationOverride?: string
      }>
      conversationRounds?: number
      skipConversationAI?: boolean
      connectorId?: string
      /** 并发度(1-10),默认 3 */
      concurrency?: number
    },
    handlers: SeedProgressHandlers,
    signal?: AbortSignal,
  ): Promise<void> => {
    const res = await fetch(`${BASE_URL}/activities/${activityId}/seed-users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(req),
      signal,
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as any).error || '造数失败')
    }
    if (!res.body) throw new Error('浏览器不支持流式响应')

    await readSSE(
      res.body,
      (ev: SSEEvent) => {
        switch (ev.type) {
          case 'start':
            handlers.onStart?.(ev as unknown as SeedProgressStart)
            break
          case 'item':
            handlers.onItem?.(ev as unknown as SeedProgressItem)
            break
          case 'summary':
            handlers.onSummary?.(ev as unknown as SeedProgressSummary)
            break
          case 'error':
            handlers.onError?.(String((ev as { message?: unknown }).message || '未知错误'))
            break
          case 'done':
            handlers.onDone?.()
            break
        }
      },
      signal,
    )
  },

  deleteSeedUsers: async (): Promise<{ deleted: Record<string, number> }> => {
    const res = await fetch(`${BASE_URL}/pool/seed-users`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as any).error || '清理失败')
    }
    return res.json()
  },

  deleteSeedUser: async (userId: string): Promise<{ deleted: Record<string, number> }> => {
    const res = await fetch(`${BASE_URL}/pool/seed-users/${userId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as any).error || '删除失败')
    }
    return res.json()
  },

  listSeedUsers: async (): Promise<{
    users: Array<{
      userId: string
      questionnaireId: string
      nickname: string
      gender: string
      age: number
      occupation: string
      profileStatus: string
      memberStatus: string
      tagsCount: number
      recCount: number
      encounterCount: number
      createdAt: string
    }>
  }> => {
    const res = await fetch(`${BASE_URL}/pool/seed-users`, {
      headers: { ...getAuthHeader() },
    })
    if (!res.ok) {
      throw new Error('加载 seed 用户列表失败')
    }
    return res.json()
  },

  // ---- Chat Templates ----
  chatTemplates: {
    list: async (page = 1, pageSize = 20): Promise<PaginatedResponse<ChatTemplate>> => {
      const res = await fetch(`${BASE_URL}/chat-templates?page=${page}&pageSize=${pageSize}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    get: async (id: string): Promise<ChatTemplate> => {
      const res = await fetch(`${BASE_URL}/chat-templates/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateChatTemplateRequest): Promise<ChatTemplate> => {
      const res = await fetch(`${BASE_URL}/chat-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateChatTemplateRequest): Promise<ChatTemplate> => {
      const res = await fetch(`${BASE_URL}/chat-templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    batchSortOrder: async (updates: { id: string; sortOrder: number }[]): Promise<void> => {
      const res = await fetch(`${BASE_URL}/chat-templates/batch-sort-order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ updates }),
      })
      if (!res.ok) throw new Error('排序更新失败')
    },
  },

  // ---- Analyze Templates ----
  analyzeTemplates: {
    list: async (page = 1, pageSize = 20): Promise<PaginatedResponse<AnalyzeTemplate>> => {
      const res = await fetch(`${BASE_URL}/analyze-templates?page=${page}&pageSize=${pageSize}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    get: async (id: string): Promise<AnalyzeTemplate> => {
      const res = await fetch(`${BASE_URL}/analyze-templates/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateAnalyzeTemplateRequest): Promise<AnalyzeTemplate> => {
      const res = await fetch(`${BASE_URL}/analyze-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateAnalyzeTemplateRequest): Promise<AnalyzeTemplate> => {
      const res = await fetch(`${BASE_URL}/analyze-templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/analyze-templates/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ---- Analyze Tasks ----
  analyzeTasks: {
    list: async (page = 1, pageSize = 20, userId?: string, status?: string): Promise<PaginatedResponse<AnalyzeTask>> => {
      let url = `${BASE_URL}/analyze-tasks?page=${page}&pageSize=${pageSize}`
      if (userId) url += `&userId=${userId}`
      if (status) url += `&status=${status}`
      const res = await fetch(url, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    get: async (taskId: string): Promise<AnalyzeTask> => {
      const res = await fetch(`${BASE_URL}/analyze-tasks/${taskId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
  },

  // ---- System Configs ----
  configs: {
    list: async (): Promise<{ configs: SystemConfig[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/configs`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    get: async (key: string): Promise<SystemConfig> => {
      const res = await fetch(`${BASE_URL}/configs/${key}`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    create: async (data: CreateConfigRequest): Promise<SystemConfig> => {
      const res = await fetch(`${BASE_URL}/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (key: string, data: UpdateConfigRequest): Promise<SystemConfig> => {
      const res = await fetch(`${BASE_URL}/configs/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (key: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/configs/${key}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
    getHistory: async (key: string): Promise<{ history: ConfigHistory[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/configs/${key}/history`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
  },

  // ---- Category Forests ----
  categoryForests: {
    list: async (): Promise<{ forests: CategoryForest[] }> => {
      const res = await fetch(`${BASE_URL}/category-forests`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    create: async (data: CreateForestRequest): Promise<CategoryForest> => {
      const res = await fetch(`${BASE_URL}/category-forests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
  },

  // ---- Category Tree ----
  categoryTree: {
    get: async (forestId: string): Promise<{ tree: CategoryTreeNode[] }> => {
      const res = await fetch(`${BASE_URL}/category-tree?forestId=${forestId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    createFolder: async (data: { forestId: string; parentId?: string; name: string; description?: string; sortOrder?: number }): Promise<CategoryTreeNode> => {
      const res = await fetch(`${BASE_URL}/category-tree`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    createLeaf: async (data: {
      forestId: string
      parentId?: string
      name: string
      value?: string
      description?: string
      extractionPrompt?: string
      sortOrder?: number
      enabled?: boolean
    }): Promise<CategoryTreeNode> => {
      const res = await fetch(`${BASE_URL}/category-tree/leaf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: { name?: string; description?: string; sortOrder?: number; enabled?: boolean }): Promise<CategoryTreeNode> => {
      const res = await fetch(`${BASE_URL}/category-tree/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
    batchSortOrder: async (updates: { id: string; sortOrder: number }[]): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/batch-sort-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ updates }),
      })
      if (!res.ok) throw new Error('排序更新失败')
    },
    batchDelete: async (ids: string[]): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/batch-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ ids }),
      })
      if (!res.ok) throw new Error('批量删除失败')
    },
    batchEnabled: async (ids: string[], enabled: boolean): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/batch-enabled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ ids, enabled }),
      })
      if (!res.ok) throw new Error('批量操作失败')
    },
  },

  // ---- Category Types ----
  categoryTypes: {
    list: async (forestId: string): Promise<{ categoryTypes: CategoryType[] }> => {
      const res = await fetch(`${BASE_URL}/category-types?forestId=${forestId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateCategoryTypeRequest): Promise<CategoryType> => {
      const res = await fetch(`${BASE_URL}/category-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
  },

  // ---- Organizers ----
  organizers: {
    list: async (page = 1, pageSize = 100): Promise<PaginatedResponse<Organizer>> => {
      const res = await fetch(`${BASE_URL}/organizers?page=${page}&pageSize=${pageSize}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: { userId: string; orgName: string; contactName?: string; contactPhone?: string }): Promise<Organizer> => {
      const res = await fetch(`${BASE_URL}/organizers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    updateStatus: async (id: string, status: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/organizers/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ status }),
      })
      if (!res.ok) throw new Error('状态更新失败')
    },
  },

  // ---- Activities ----
  activities: {
    list: async (page = 1, pageSize = 20, opts?: { status?: string; mode?: string }): Promise<PaginatedResponse<Activity>> => {
      let url = `${BASE_URL}/activities?page=${page}&pageSize=${pageSize}`
      if (opts?.status) url += `&status=${opts.status}`
      if (opts?.mode) url += `&mode=${opts.mode}`
      const res = await fetch(url, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    get: async (id: string): Promise<ActivityDetail> => {
      const res = await fetch(`${BASE_URL}/activities/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateActivityRequest): Promise<Activity> => {
      const res = await fetch(`${BASE_URL}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateActivityRequest): Promise<Activity> => {
      const res = await fetch(`${BASE_URL}/activities/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    updateStatus: async (id: string, status: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/activities/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ status }),
      })
      if (!res.ok) throw new Error('状态更新失败')
    },
    listQuestionnaires: async (
      activityId: string,
      params?: {
        page?: number
        pageSize?: number
        status?: string
        wechat?: string
        profile?: string
        search?: string
      },
    ): Promise<QuestionnaireListResponse> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
      if (params?.status) qs.set('status', params.status)
      if (params?.wechat) qs.set('wechat', params.wechat)
      if (params?.profile) qs.set('profile', params.profile)
      if (params?.search) qs.set('search', params.search)
      const res = await fetch(`${BASE_URL}/activities/${activityId}/questionnaires?${qs}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listReports: async (activityId: string): Promise<{ items: ReportSummary[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/reports`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    getReport: async (activityId: string, reportId: string): Promise<ReportDetail> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/reports/${reportId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    rematch: async (id: string): Promise<RematchResult> => {
      const res = await fetch(`${BASE_URL}/activities/${id}/rematch`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    clearMatchData: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/activities/${id}/clear-match-data`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      await handleResponse(res)
    },
    listRecommendations: async (id: string): Promise<{ recommendations: MatchRecommendation[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/activities/${id}/recommendations`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listMatchResults: async (id: string): Promise<{ matchResults: MatchResultItem[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/activities/${id}/match-results`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listMatchLogs: async (id: string, userId?: string): Promise<{ logs: MatchPipelineLog[]; total: number }> => {
      const qs = userId ? `?userId=${encodeURIComponent(userId)}` : ''
      const res = await fetch(`${BASE_URL}/activities/${id}/match-logs${qs}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    getQuestionnaire: async (id: string): Promise<QuestionnaireDetail> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    deleteQuestionnaire: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('移除失败')
    },
    updateQuestionnaire: async (id: string, data: { wechatAdded: boolean }): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('更新失败')
    },
    setQuestionnaireDisabled: async (id: string, disabled: boolean): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}/disabled`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ disabled }),
      })
      if (!res.ok) throw new Error('操作失败')
    },
    promoteQuestionnaire: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}/promote`, {
        method: 'PUT',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || '转正失败')
      }
    },
    getUserProfile: async (activityId: string, userId: string): Promise<UserProfileDetail> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/users/${userId}/profile`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    regenerateProfiles: async (activityId: string, force = false): Promise<{ enqueued: number; skipped: number }> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/regenerate-profiles?force=${force}`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    regenerateProfileByUser: async (activityId: string, userId: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/users/${userId}/regenerate-profile`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('操作失败')
    },
    triggerMatch: async (activityId: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/trigger-match`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('操作失败')
    },

    matchUsers: async (activityId: string, userId: string, targetUserId?: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/match-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ userId, targetUserId: targetUserId || '' }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || '操作失败')
      }
    },

    deleteMatchPair: async (activityId: string, userA: string, userB: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/delete-match-pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ userA, userB }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || '删除失败')
      }
    },

    exportMatchData: async (activityId: string): Promise<Blob> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/match/export`, {
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `导出失败: ${res.status}`)
      }
      return res.blob()
    },

    // ===== Feedback =====

    // ===== Feedback =====
    getFeedbackOverview: async (activityId: string): Promise<FeedbackOverview> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/feedback/overview`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listFeedbackRatings: async (activityId: string): Promise<{ ratings: AdminRatingItem[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/feedback/ratings`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listFeedbackMessages: async (activityId: string): Promise<{ messages: AdminMessageItem[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/feedback/messages`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listFeedbackSelections: async (activityId: string): Promise<{ selections: AdminSelectionItem[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/feedback/selections`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },

    // ===== Check-In =====
    createCheckInSession: async (activityId: string, name: string): Promise<CheckInSession> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/checkin-sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ name }),
      })
      return handleResponse(res)
    },
    listCheckInSessions: async (activityId: string): Promise<{ sessions: CheckInSession[] }> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/checkin-sessions`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listCheckInRecords: async (activityId: string, sessionId?: string): Promise<{ records: CheckInRecord[] }> => {
      let url = `${BASE_URL}/activities/${activityId}/checkins`
      if (sessionId) url += `?sessionId=${sessionId}`
      const res = await fetch(url, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    getCheckInOverview: async (activityId: string): Promise<CheckInOverview> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/checkin/overview`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
  },

  // ===== Tool Definitions =====
  toolDefinitions: {
    list: async (): Promise<{ tools: ToolDefinition[] }> => {
      const res = await fetch(`${BASE_URL}/tool-definitions`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    get: async (id: string): Promise<ToolDefinition> => {
      const res = await fetch(`${BASE_URL}/tool-definitions/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateToolDefRequest): Promise<ToolDefinition> => {
      const res = await fetch(`${BASE_URL}/tool-definitions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateToolDefRequest): Promise<ToolDefinition> => {
      const res = await fetch(`${BASE_URL}/tool-definitions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/tool-definitions/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ===== Connectors (expanded) =====
  connectors: {
    list: async (): Promise<{ connectors: LLMConnector[] }> => {
      const res = await fetch(`${BASE_URL}/llm/connectors`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    get: async (id: string): Promise<LLMConnector> => {
      const res = await fetch(`${BASE_URL}/llm/connectors/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateConnectorRequest): Promise<LLMConnector> => {
      const res = await fetch(`${BASE_URL}/llm/connectors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateConnectorRequest): Promise<LLMConnector> => {
      const res = await fetch(`${BASE_URL}/llm/connectors/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/connectors/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ===== Models =====
  models: {
    list: async (): Promise<{ models: LLMModel[] }> => {
      const res = await fetch(`${BASE_URL}/llm/models`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateModelRequest): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/models`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('创建失败')
    },
    update: async (id: string, data: UpdateModelRequest): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/models/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('更新失败')
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/models/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ===== Providers =====
  providers: {
    list: async (): Promise<{ providers: LLMProvider[] }> => {
      const res = await fetch(`${BASE_URL}/llm/providers`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateProviderRequest): Promise<LLMProvider> => {
      const res = await fetch(`${BASE_URL}/llm/providers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateProviderRequest): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/providers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('更新失败')
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/providers/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ===== TTS Voice =====
  ttsVoices: {
    list: async (provider?: string): Promise<{ ttsVoices: TTSVoice[] }> => {
      const url = provider ? `${BASE_URL}/tts-voices?provider=${encodeURIComponent(provider)}` : `${BASE_URL}/tts-voices`
      const res = await fetch(url, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    create: async (data: CreateTTSVoiceRequest): Promise<TTSVoice> => {
      const res = await fetch(`${BASE_URL}/tts-voices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateTTSVoiceRequest): Promise<void> => {
      const res = await fetch(`${BASE_URL}/tts-voices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('更新失败')
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/tts-voices/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ===== FlowControllers =====
  flowControllers: {
    list: async (params?: { system_id?: string; status?: number }): Promise<{ flowControllers: FlowController[] }> => {
      const qs = new URLSearchParams()
      if (params?.system_id) qs.set('system_id', params.system_id)
      if (params?.status !== undefined) qs.set('status', String(params.status))
      const url = qs.toString() ? `${BASE_URL}/flow-controllers?${qs}` : `${BASE_URL}/flow-controllers`
      const res = await fetch(url, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    create: async (data: CreateFlowControllerRequest): Promise<FlowController> => {
      const res = await fetch(`${BASE_URL}/flow-controllers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateFlowControllerRequest): Promise<void> => {
      const res = await fetch(`${BASE_URL}/flow-controllers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('更新失败')
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/flow-controllers/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
    getState: async (controllerId: string, contextId: string): Promise<{ data: FlowControllerState }> => {
      const res = await fetch(
        `${BASE_URL}/flow-controllers/by-controller/${controllerId}/state?context_id=${encodeURIComponent(contextId)}`,
        { headers: { ...getAuthHeader() } }
      )
      return handleResponse(res)
    },
    getSessionLogs: async (contextId: string): Promise<{ data: FlowControllerLog[] }> => {
      const res = await fetch(
        `${BASE_URL}/flow-controllers/sessions/${encodeURIComponent(contextId)}/logs`,
        { headers: { ...getAuthHeader() } }
      )
      return handleResponse(res)
    },
    getSessionMessages: async (contextId: string): Promise<{ data: SessionMessage[] }> => {
      const res = await fetch(
        `${BASE_URL}/flow-controllers/sessions/${encodeURIComponent(contextId)}/messages`,
        { headers: { ...getAuthHeader() } }
      )
      return handleResponse(res)
    },
    listSessions: async (params?: {
      controllerId?: string
      userId?: string
      page?: number
      pageSize?: number
    }): Promise<PaginatedResponse<ControllerSession>> => {
      const qs = new URLSearchParams()
      if (params?.controllerId) qs.set('controller_id', params.controllerId)
      if (params?.userId) qs.set('user_id', params.userId)
      if (params?.page) qs.set('page', String(params.page))
      if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
      const res = await fetch(`${BASE_URL}/flow-controllers/sessions?${qs}`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
  },

  // ===== API Keys =====
  apiKeys: {
    list: async (): Promise<{ apiKeys: LLMApiKey[] }> => {
      const res = await fetch(`${BASE_URL}/llm/api-keys`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateAPIKeyRequest): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('创建失败')
    },
    update: async (id: string, data: UpdateAPIKeyRequest): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/api-keys/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('更新失败')
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/llm/api-keys/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ===== Queue & Scheduler Monitor =====
  queue: {
    overview: async (): Promise<{ queues: QueueInfo[] }> => {
      const res = await fetch(`${BASE_URL}/queue/overview`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    listTasks: async (
      q: string,
      state: string,
      page = 1,
      pageSize = 20
    ): Promise<PaginatedResponse<QueueTask>> => {
      const res = await fetch(
        `${BASE_URL}/queue/${q}/tasks?state=${state}&page=${page}&pageSize=${pageSize}`,
        { headers: { ...getAuthHeader() } }
      )
      return handleResponse(res)
    },
    getTask: async (q: string, id: string): Promise<QueueTask> => {
      const res = await fetch(`${BASE_URL}/queue/${q}/tasks/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    deleteTask: async (q: string, id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/queue/${q}/tasks/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('删除失败')
    },
    runTask: async (q: string, id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/queue/${q}/tasks/${id}/run`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('重试失败')
    },
    archiveTask: async (q: string, id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/queue/${q}/tasks/${id}/archive`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('归档失败')
    },
    deleteAll: async (q: string, state: string): Promise<{ deleted: number }> => {
      const res = await fetch(`${BASE_URL}/queue/${q}/tasks?state=${state}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    pauseQueue: async (q: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/queue/${q}/pause`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('暂停失败')
    },
    resumeQueue: async (q: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/queue/${q}/resume`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok) throw new Error('恢复失败')
    },
    schedulers: async (): Promise<{ schedulers: SchedulerInfo[] }> => {
      const res = await fetch(`${BASE_URL}/queue/schedulers`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
  },

  // ===== Prompt Test (Playground) =====
  promptTest: {
    run: async (payload: PromptTestRunRequest): Promise<ReadableStream<Uint8Array>> => {
      const res = await fetch(`${BASE_URL}/prompt-test/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `请求失败: ${res.status}`)
      }
      if (!res.body) throw new Error('浏览器不支持流式响应')
      return res.body
    },
    submitBatch: async (
      activityId: string,
      payload: {
        systemPrompt: string
        userPrompt: string
        connectorId: string
        timeoutSeconds: number
        disableThinking: boolean
        sharedVariables?: Record<string, string>
      }
    ): Promise<PromptTestBatch> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/prompt-test-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      })
      return handleResponse(res)
    },
    getBatch: async (batchId: string): Promise<PromptTestBatch> => {
      const res = await fetch(`${BASE_URL}/prompt-test-batches/${batchId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listBatches: async (activityId: string, page = 1, pageSize = 50): Promise<PromptTestBatchList> => {
      const res = await fetch(
        `${BASE_URL}/activities/${activityId}/prompt-test-batches?page=${page}&pageSize=${pageSize}`,
        { headers: { ...getAuthHeader() } }
      )
      return handleResponse(res)
    },
    retryBatch: async (batchId: string): Promise<PromptTestBatch> => {
      const res = await fetch(`${BASE_URL}/prompt-test-batches/${batchId}/retry`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    // doc 71:批量匹配测试
    submitMatchBatch: async (
      activityId: string,
      payload: {
        systemPrompt: string
        userPrompt: string
        connectorId: string
        timeoutSeconds: number
        disableThinking: boolean
        pairs: { userIdA: string; userIdB: string }[]
        sharedVariables?: Record<string, string>
      }
    ): Promise<PromptTestMatchBatch> => {
      const res = await fetch(`${BASE_URL}/activities/${activityId}/prompt-test-match-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      })
      return handleResponse(res)
    },
    getMatchBatch: async (batchId: string): Promise<PromptTestMatchBatch> => {
      const res = await fetch(`${BASE_URL}/prompt-test-match-batches/${batchId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    listMatchBatches: async (activityId: string, page = 1, pageSize = 50): Promise<PromptTestMatchBatchList> => {
      const res = await fetch(
        `${BASE_URL}/activities/${activityId}/prompt-test-match-batches?page=${page}&pageSize=${pageSize}`,
        { headers: { ...getAuthHeader() } }
      )
      return handleResponse(res)
    },
    retryMatchBatch: async (batchId: string): Promise<PromptTestMatchBatch> => {
      const res = await fetch(`${BASE_URL}/prompt-test-match-batches/${batchId}/retry`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
  },

  // ============ Agent 测试台(Agent Harness) ============

  // 会话列表项(97 号) — 类型定义见文件顶部 export interface HarnessSessionItem

  agentHarness: {
    /** SSE 流式:agent /api/chat/turn(经 jingque-match 透传)。返回 ReadableStream。 */
    run: async (payload: {
      context_id: string
      // doc 121:run_as 控制工具查询视角(空=本人);会话归属恒 admin
      run_as?: string
      user_text?: string
      template_id: string
      flow_controller_id?: string
      connector_id?: string // doc 117:显式 connector 覆盖(空=走模板/默认)
      tools?: any[]
      tool_result?: { call_id: string; result?: any; error?: string } | null
      // doc 119:工具轮上限到顶后的续跑标志(纯 UI 触发;不带 user_text/tool_result)
      continue_after_max_rounds?: boolean
      // doc 123-Fix:手动压缩标志(同 continue_after_max_rounds 先例,body 标志位复用本 SSE 通道)
      compact?: boolean
    }): Promise<ReadableStream<Uint8Array>> => {
      const res = await fetch(`${BASE_URL}/agent-harness/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `请求失败: ${res.status}`)
      }
      if (!res.body) throw new Error('浏览器不支持流式响应')
      return res.body
    },

    /** 手动测单个 agent 工具:agent /api/tools/invoke(经 registry.execute 真实执行)。 */
    invokeTool: async (payload: {
      name: string
      args: Record<string, any>
      context_id: string
      user_id?: string
      template_id: string
    }): Promise<{ result: string; parsed?: any }> => {
      const res = await fetch(
        `${BASE_URL}/agent-harness/tools/${encodeURIComponent(payload.name)}/invoke`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify({
            name: payload.name,
            args: payload.args,
            context_id: payload.context_id,
            user_id: payload.user_id,
            template_id: payload.template_id,
          }),
        }
      )
      return handleResponse(res)
    },

    // ── doc 122:VFS 文件树结构化浏览 + 挂载点管理 ──

    /** 结构化 FS 浏览(ls 返回 entries / read 返回 content);agent 内部走 _fs_dispatch。 */
    fsBrowse: async (payload: {
      action: 'ls' | 'read'
      path: string
      context_id?: string
      template_id?: string
      run_as?: string
      offset?: number
      limit?: number
    }): Promise<{
      path: string
      envelope?: { data?: { entries?: Array<{ name: string; type: string; summary?: string }>; total?: number; content?: string; total_lines?: number } }
      error?: { error: string; provider_status?: number; available_services?: string[] }
    }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/fs/browse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      })
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    /** 挂载点列表(含 enabled)。 */
    fsMounts: async (): Promise<{
      mounts: Array<{ prefix: string; kind: 'http' | 'local'; target: string; fs_path: string; enabled: boolean }>
    }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/fs/mounts`, { headers: getAuthHeader() })
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    /** 启停某挂载前缀(全局,agent Redis 存)。 */
    fsSetMount: async (prefix: string, enabled: boolean): Promise<{ prefix: string; enabled: boolean }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/fs/mounts`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ prefix, enabled }),
      })
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    /** doc 125-Fix4:挂载缓存诊断快照(全局 + 模板两层)。 */
    fsMountsCache: async (): Promise<{
      global: { disabled: string[]; age_seconds: number | null; ttl_seconds: number }
      templates: Array<{
        template_id: string
        restricted: boolean
        allowed: string[] | null
        disabled: string[]
        age_seconds: number
        ttl_seconds: number
      }>
    }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/fs/mounts/cache`, {
        headers: { ...getAuthHeader() },
      })
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    /** doc 125-Fix4:清模板缓存(templateId = 清单条,不传 = 清全部,返回清除条数)。 */
    fsMountsCacheClear: async (templateId?: string): Promise<{ cleared: number; template_id?: string }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/fs/mounts/cache`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(templateId ? { template_id: templateId } : {}),
      })
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    // ── 上下文压缩(doc 123, doc 123-Fix)──
    // 手动压缩已改用 run({compact:true}) 复用 turn SSE 通道(无独立端点)。
    // 以下保留状态徽标 + 配置端点(短 JSON,无超时问题)。

    /** 上下文用量状态(给 header 徽标)。 */
    compactionStatus: async (contextId: string): Promise<{
      est_tokens: number
      context_tokens: number
      ratio: number
      trigger_ratio: number
      enabled: boolean
      basis: 'real' | 'estimate'
    }> => {
      const res = await fetch(
        `${BASE_URL}/agent-harness/compaction/status?context_id=${encodeURIComponent(contextId)}`,
        { headers: getAuthHeader() },
      )
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    /** 获取压缩配置。 */
    compactionConfigGet: async (): Promise<{
      enabled: boolean
      trigger_ratio: number
      keep_tokens: number
      min_messages: number
      cooldown_messages: number
    }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/compaction/config`, {
        headers: getAuthHeader(),
      })
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    /** 更新压缩配置(数值钳制在 agent 端完成)。 */
    compactionConfigSet: async (
      partial: Partial<{
        enabled: boolean
        trigger_ratio: number
        keep_tokens: number
        min_messages: number
        cooldown_messages: number
      }>,
    ): Promise<{
      enabled: boolean
      trigger_ratio: number
      keep_tokens: number
      min_messages: number
      cooldown_messages: number
    }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/compaction/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(partial),
      })
      const j = await handleResponse<{ data: any }>(res)
      return j.data
    },

    /** 读回 chat_messages 历史。 */
    loadSession: async (contextId: string, userId?: string): Promise<{ messages: any[] }> => {
      // doc 102:user_id 改 query 透传(原 X-User-ID 自定义头触发 CORS 预检失败,
      // 且会被 nginx proxy_set_header 覆盖为登录用户)
      const qs = new URLSearchParams()
      if (userId) qs.set('user_id', userId)
      const res = await fetch(
        `${BASE_URL}/agent-harness/sessions/${encodeURIComponent(contextId)}/load?${qs.toString()}`,
        { headers: getAuthHeader() },
      )
      // doc 106:刺壳 — jingque-match handler 返回 {data:{messages}};96 号当时未剥,
      // 102 修 CORS 后请求 200 但 data.messages 仍为 undefined → 静默空时间线
      const j = await handleResponse<{ data: { messages: any[] } }>(res)
      return j.data
    },

    // ── 会话管理(97 号:列表 / 重命名 / AI 命名 / 删除;101 号:获取)──

    /** doc 101:获取/复用 harness 会话(backend 空会话复用,无则新建)。 */
    createSession: async (
      userId: string,
    ): Promise<{ session_id: string; is_new: boolean; message_count: number }> => {
      const res = await fetch(`${BASE_URL}/agent-harness/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ user_id: userId }),
      })
      const j = await handleResponse<{ data: { session_id: string; is_new: boolean; message_count: number } }>(res)
      return j.data
    },

    /** 列出指定用户 voicechat 系统下的会话。 */
    listSessions: async (
      userId: string,
      page = 1,
      pageSize = 50,
    ): Promise<{ data: HarnessSessionItem[]; total: number; page: number; page_size: number }> => {
      const qs = new URLSearchParams({
        user_id: userId,
        page: String(page),
        page_size: String(pageSize),
      })
      const res = await fetch(
        `${BASE_URL}/agent-harness/sessions?${qs.toString()}`,
        { headers: getAuthHeader() },
      )
      return handleResponse(res)
    },

    /** 重命名会话。 */
    renameSession: async (
      contextId: string,
      name: string,
      userId: string,
    ): Promise<void> => {
      const res = await fetch(
        `${BASE_URL}/agent-harness/sessions/${encodeURIComponent(contextId)}/name`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify({ user_id: userId, name }),
        },
      )
      await handleResponse(res)
    },

    /** AI 根据首轮一问一答生成短标题(后端会同时持久化)。 */
    autoNameSession: async (
      contextId: string,
      templateId: string,
      userId: string,
    ): Promise<{ name: string; error?: string }> => {
      const res = await fetch(
        `${BASE_URL}/agent-harness/sessions/${encodeURIComponent(contextId)}/auto-name`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify({ user_id: userId, template_id: templateId }),
        },
      )
      // doc 106:刺壳 — handler 返回 {data:{name,error}};此前未剥导致 name/error 均为 undefined,
      // 「已命名」永不触发,固定走「AI 命名失败」
      const j = await handleResponse<{ data: { name: string; error?: string } }>(res)
      return j.data
    },

    /** 删除会话。 */
    deleteSession: async (contextId: string, userId: string): Promise<void> => {
      const qs = new URLSearchParams({ user_id: userId })
      const res = await fetch(
        `${BASE_URL}/agent-harness/sessions/${encodeURIComponent(contextId)}?${qs.toString()}`,
        { method: 'DELETE', headers: getAuthHeader() },
      )
      await handleResponse(res)
    },
  },

  // ============ Education Verification ============

  eduVerify: {
    list: async (
      page = 1,
      pageSize = 20,
      status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending',
    ): Promise<EduVerifyListResponse> => {
      const res = await fetch(
        `${BASE_URL}/edu-verify/list?page=${page}&page_size=${pageSize}&status=${status}`,
        { headers: { ...getAuthHeader() } },
      )
      const json = await handleResponse<{ data: EduVerifyListResponse }>(res)
      return json.data
    },
    getCertificateURL: async (id: string): Promise<EduCertificateURL> => {
      const res = await fetch(`${BASE_URL}/edu-verify/${id}/certificate`, {
        headers: { ...getAuthHeader() },
      })
      const json = await handleResponse<{ data: EduCertificateURL }>(res)
      return json.data
    },
    review: async (
      id: string,
      payload: {
        action: 'approve' | 'reject'
        actual_education?: string
        actual_school?: string
        reject_reason?: string
      },
    ): Promise<void> => {
      const res = await fetch(`${BASE_URL}/edu-verify/${id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      })
      await handleResponse(res)
    },
  },
}

// ============ Education Verification Types ============

export interface EduVerifyItem {
  id: string
  user_id: string
  verify_no: string
  status: string
  declared_education: string
  declared_school: string
  declared_school_type?: string
  certificate_object_key: string
  submitted_at: string
}

export interface EduVerifyListResponse {
  items: EduVerifyItem[]
  total: number
  page: number
  page_size: number
}

export interface EduCertificateURL {
  url: string
  expire_at: number
}
